import { z } from 'zod';
export { parseOsis, parseOsisRange, refMatches, osisBook, } from './osis.js';
export const EntryTypeSchema = z.enum([
    'place',
    'figure',
    'concept',
    'map',
    'customs',
    'event',
    'book',
    'culture',
    'object',
    'term',
]);
export const SlugSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'kebab-case slug, lower-ASCII');
export const LangTagSchema = z
    .string()
    .min(2)
    .max(35)
    .regex(/^[a-zA-Z][a-zA-Z0-9-]*$/, 'BCP-47 tag');
export const OsisRefSchema = z
    .string()
    .regex(/^[1-3]?[A-Za-z]+(\.[0-9]+(\.[0-9]+)?)?(-[1-3]?[A-Za-z]+(\.[0-9]+(\.[0-9]+)?)?)?$/, 'OSIS reference')
    .max(64);
export const LicenseRefSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    url: z.string().url().optional(),
});
export const SourceRefSchema = z.object({
    title: z.string().min(1),
    author: z.string().optional(),
    year: z.number().int().min(0).max(2100).optional(),
    license: LicenseRefSchema,
    pageOrSection: z.string().optional(),
});
const LocaleString = z.object({
    lang: LangTagSchema,
    value: z.string().min(1),
});
const LocaleText = z.object({
    lang: LangTagSchema,
    value: z.string().min(1),
});
export const BlurbSchema = z.object({
    lang: LangTagSchema,
    text: z.string().min(1).max(280),
    imageHash: z.string().optional(),
    href: z.string().min(1),
});
export const MediaKindSchema = z.enum(['image', 'video', 'audio', 'map-svg', 'map-vector']);
export const MediaSchema = z.object({
    kind: MediaKindSchema,
    src: z.string().min(1),
    poster: z.string().optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
    caption: z.array(LocaleString).default([]),
    credit: z.string().min(1),
    license: z.string().min(1),
    durationMs: z.number().int().nonnegative().optional(),
});
export const ComparatorTranslationSchema = z.object({
    lang: LangTagSchema,
    value: z.string().min(1),
    reference: z.string().optional(),
});
export const ComparatorSchema = z.object({
    kind: z.enum(['distance', 'size', 'currency', 'time']),
    value: z.string().min(1),
    reference: z.string().optional(),
    translations: z.array(ComparatorTranslationSchema).default([]),
});
export const PronunciationSchema = z.object({
    lang: LangTagSchema,
    ipa: z.string().min(1),
    audioUrl: z.string().min(1).optional(),
});
export const EntryStatusSchema = z.enum(['draft', 'review', 'published']);
export const NarrationSchema = z.object({
    lang: LangTagSchema,
    audioUrl: z.string().min(1),
    durationMs: z.number().int().positive(),
    bytes: z.number().int().positive(),
    segments: z
        .array(z.object({
        offsetMs: z.number().int().nonnegative(),
        text: z.string().min(1),
    }))
        .optional(),
    voice: z
        .object({
        kind: z.enum(['human', 'tts']),
        provider: z.string().optional(),
        attribution: z.string().optional(),
    })
        .default({ kind: 'human' }),
});
const EntryCoreSchema = z.object({
    type: EntryTypeSchema,
    slug: SlugSchema,
    primary: z.object({
        lang: LangTagSchema,
        name: z.string().min(1),
    }),
    i18n: z.array(LocaleString).default([]),
    blurb: z.array(BlurbSchema).default([]),
    article: z.array(LocaleText).default([]),
    longform: z.array(LocaleText).default([]),
    media: z.array(MediaSchema).default([]),
    comparators: z.array(ComparatorSchema).default([]),
    pronunciations: z.array(PronunciationSchema).default([]),
    blurbNarration: z.array(NarrationSchema).default([]),
    articleNarration: z.array(NarrationSchema).default([]),
    refs: z.array(OsisRefSchema).default([]),
    related: z
        .array(z.object({
        type: EntryTypeSchema,
        slug: SlugSchema,
        reason: z.string().optional(),
    }))
        .default([]),
    sources: z.array(SourceRefSchema).default([]),
    status: EntryStatusSchema.default('published'),
    lockedBy: z.string().optional(),
    updatedAt: z.string().datetime().optional(),
    tags: z.array(z.string().min(1).max(32)).default([]),
});
export const PlaceSchema = EntryCoreSchema.extend({
    type: z.literal('place'),
    coord: z
        .object({
        lat: z.number().min(-90).max(90),
        lon: z.number().min(-180).max(180),
    })
        .optional(),
    modernName: z.string().optional(),
    region: z.string().optional(),
    era: z.string().optional(),
});
export const FigureSchema = EntryCoreSchema.extend({
    type: z.literal('figure'),
    role: z.string().optional(),
    bornDied: z.string().optional(),
    family: z
        .object({
        father: z.string().optional(),
        mother: z.string().optional(),
        spouse: z.array(z.string()).default([]),
        children: z.array(z.string()).default([]),
    })
        .optional(),
});
export const ConceptSchema = EntryCoreSchema.extend({
    type: z.literal('concept'),
    cluster: z
        .enum(['theological', 'relational', 'ethical', 'narrative', 'imagery'])
        .optional(),
});
export const MapSchema = EntryCoreSchema.extend({
    type: z.literal('map'),
    era: z.string().min(1),
    bbox: z.object({
        n: z.number().min(-90).max(90),
        s: z.number().min(-90).max(90),
        e: z.number().min(-180).max(180),
        w: z.number().min(-180).max(180),
    }),
    placeSlugs: z.array(SlugSchema).default([]),
    vectorUrl: z.string().min(1).optional(),
});
export const CustomsSchema = EntryCoreSchema.extend({
    type: z.literal('customs'),
    topic: z.string().optional(),
});
export const EventSchema = EntryCoreSchema.extend({
    type: z.literal('event'),
    bce: z
        .object({
        start: z.number().int(),
        end: z.number().int(),
    })
        .optional(),
    participants: z
        .array(z.object({
        type: EntryTypeSchema,
        slug: SlugSchema,
    }))
        .default([]),
    locations: z.array(SlugSchema).default([]),
});
export const BookSchema = EntryCoreSchema.extend({
    type: z.literal('book'),
    testament: z.enum(['ot', 'nt']),
    genre: z.enum(['law', 'history', 'wisdom', 'prophecy', 'gospel', 'epistle', 'apocalyptic']),
    position: z.number().int().min(1).max(66),
    chapters: z.number().int().positive(),
    outline: z
        .array(z.object({
        ref: OsisRefSchema,
        label: z.string().min(1),
    }))
        .default([]),
});
export const CultureSchema = EntryCoreSchema.extend({
    type: z.literal('culture'),
    era: z.string().optional(),
    homeland: z.array(SlugSchema).default([]),
});
export const ObjectSchema = EntryCoreSchema.extend({
    type: z.literal('object'),
    category: z
        .enum(['coin', 'weight', 'measure', 'instrument', 'tool', 'vessel', 'garment', 'document'])
        .optional(),
});
export const TermSchema = EntryCoreSchema.extend({
    type: z.literal('term'),
    strong: z.string().min(1).optional(),
    transliteration: z.string().min(1),
    originalText: z.string().min(1),
    domain: z.string().min(1).optional(),
    termLanguage: z.enum(['hebrew', 'greek', 'aramaic']),
    category: z.string().min(1),
});
export const EntrySchema = z.discriminatedUnion('type', [
    PlaceSchema,
    FigureSchema,
    ConceptSchema,
    MapSchema,
    CustomsSchema,
    EventSchema,
    BookSchema,
    CultureSchema,
    ObjectSchema,
    TermSchema,
]);
export const EntryIndexRowSchema = z.object({
    type: EntryTypeSchema,
    slug: SlugSchema,
    name: z.string(),
    refs: z.array(OsisRefSchema),
});
export const BlurbResponseSchema = z.object({
    type: EntryTypeSchema,
    slug: SlugSchema,
    blurb: BlurbSchema,
});
export const FullEntryResponseSchema = z.object({
    type: EntryTypeSchema,
    slug: SlugSchema,
    name: z.string(),
    primary: z.object({ lang: LangTagSchema, name: z.string() }),
    blurb: BlurbSchema.nullable(),
    article: z.string().nullable(),
    longform: z.string().nullable(),
    media: z.array(MediaSchema),
    comparators: z.array(ComparatorSchema),
    pronunciation: PronunciationSchema.nullable(),
    refs: z.array(OsisRefSchema),
    sources: z.array(SourceRefSchema),
    status: EntryStatusSchema,
});
export function blurbFor(entry, lang) {
    return entry.blurb.find((b) => b.lang === lang) ?? entry.blurb[0];
}
export function articleFor(entry, lang) {
    return entry.article.find((a) => a.lang === lang) ?? entry.article[0];
}
export function longformFor(entry, lang) {
    return entry.longform.find((a) => a.lang === lang) ?? entry.longform[0];
}
export function captionFor(media, lang) {
    return media.caption.find((c) => c.lang === lang)?.value ?? media.caption[0]?.value;
}
export function pronunciationFor(entry, lang) {
    return entry.pronunciations.find((p) => p.lang === lang) ?? entry.pronunciations[0];
}
export function nameFor(entry, lang) {
    return entry.i18n.find((n) => n.lang === lang)?.value ?? entry.primary.name;
}
export function hrefFor(entry, lang = 'en') {
    return `/${lang}/${entry.type}/${entry.slug}`;
}
// Atlas's first-class locale set. The first six (en/fr/es/pt/ar/zh) plus
// the four FCBH-priority African / Ethiopian langs (sw/ha/yo/am) shipped
// in 1.0–1.3. The 23 added in 1.7 are ElevenLabs Multilingual v2's full
// supported set minus the six already present — so the future TTS bake
// can produce a single voice (Rachel) reading every entry in 29 langs.
export const ATLAS_LANGS = [
    // FCBH priority
    'en',
    'fr',
    'ar',
    'sw',
    'ha',
    'yo',
    'am',
    'pt',
    'es',
    'zh',
    // ElevenLabs Multilingual v2 additions (1.7)
    'de',
    'it',
    'pl',
    'hi',
    'ja',
    'ko',
    'nl',
    'tr',
    'ru',
    'cs',
    'sv',
    'fi',
    'ro',
    'el',
    'ms',
    'id',
    'hr',
    'sk',
    'da',
    'bg',
    'fil',
    'ta',
    'uk',
    // Global-phone coverage (1.8) — locales not in ElevenLabs Multilingual
    // v2 but common as device system languages. Audio for these falls
    // through to the on-demand TTS route's translate-then-TTS path
    // (translation always works; audio plays only if ElevenLabs gets it,
    // otherwise Web Speech). Text is what matters here — a Vietnamese
    // phone gets a Vietnamese page.
    'vi', // Vietnamese
    'th', // Thai
    'fa', // Persian / Farsi
    'ur', // Urdu
    'he', // Hebrew
    'bn', // Bengali
    'te', // Telugu
    'mr', // Marathi
    'pa', // Punjabi
    'gu', // Gujarati
    'no', // Norwegian
    'hu', // Hungarian
    'sl', // Slovenian
    'ca', // Catalan
    'sr', // Serbian
    'sq', // Albanian
    'is', // Icelandic
    'et', // Estonian
    'lv', // Latvian
    'lt', // Lithuanian
];
export const ATLAS_HOST = 'https://fcbh-atlas.vercel.app';
const MACRO_LANGUAGE = {
    cmn: 'zh',
    yue: 'zh',
    hak: 'zh',
    arb: 'ar',
    fas: 'fa',
    per: 'fa',
    pes: 'fa',
    prs: 'fa',
    por: 'pt',
    spa: 'es',
    eng: 'en',
    fra: 'fr',
    swa: 'sw',
};
function normalizeTag(raw) {
    return raw.trim().toLowerCase().replace(/_/g, '-');
}
function tagSubtags(tag) {
    return normalizeTag(tag).split('-').filter(Boolean);
}
function tagCandidates(tag) {
    const parts = tagSubtags(tag);
    if (parts.length === 0)
        return [];
    const out = [];
    for (let i = parts.length; i > 0; i--)
        out.push(parts.slice(0, i).join('-'));
    const primary = parts[0];
    if (primary && MACRO_LANGUAGE[primary])
        out.push(MACRO_LANGUAGE[primary]);
    return out;
}
export function resolveAtlasLang(preferred, supported = ATLAS_LANGS) {
    const supportedNorm = supported.map((s) => normalizeTag(s));
    const supportedSet = new Set(supportedNorm);
    const fallback = supportedNorm[0] ?? 'en';
    const list = Array.isArray(preferred)
        ? preferred
        : preferred
            ? [preferred]
            : [];
    for (const raw of list) {
        if (!raw)
            continue;
        for (const candidate of tagCandidates(String(raw))) {
            if (supportedSet.has(candidate))
                return candidate;
            const primary = candidate.split('-')[0];
            if (primary && supportedSet.has(primary))
                return primary;
        }
    }
    return fallback;
}
function appendCommon(url, opts) {
    if (opts.ref)
        url.searchParams.set('ref', opts.ref);
    if (opts.from)
        url.searchParams.set('from', opts.from);
    if (opts.returnTo)
        url.searchParams.set('returnTo', opts.returnTo);
    if (opts.palette)
        url.searchParams.set('palette', opts.palette);
}
export function atlasHomeUrl(opts = {}) {
    const host = opts.host ?? ATLAS_HOST;
    const lang = opts.lang ?? 'en';
    const url = new URL(`/${encodeURIComponent(lang)}`, host);
    appendCommon(url, opts);
    return url.toString();
}
export function atlasEntryUrl(opts) {
    const host = opts.host ?? ATLAS_HOST;
    const lang = opts.lang ?? 'en';
    const url = new URL(`/${encodeURIComponent(lang)}/${encodeURIComponent(opts.type)}/${encodeURIComponent(opts.slug)}`, host);
    appendCommon(url, opts);
    return url.toString();
}
export function atlasTimelineUrl(opts = {}) {
    const host = opts.host ?? ATLAS_HOST;
    const lang = opts.lang ?? 'en';
    const url = new URL(`/${encodeURIComponent(lang)}/timeline`, host);
    appendCommon(url, opts);
    return url.toString();
}
export function toFullEntryResponse(entry, lang) {
    const article = articleFor(entry, lang);
    const longform = longformFor(entry, lang);
    const blurb = blurbFor(entry, lang) ?? null;
    return {
        type: entry.type,
        slug: entry.slug,
        name: nameFor(entry, lang),
        primary: entry.primary,
        blurb,
        article: article?.value ?? null,
        longform: longform?.value ?? null,
        media: entry.media,
        comparators: entry.comparators,
        pronunciation: pronunciationFor(entry, lang) ?? null,
        refs: entry.refs,
        sources: entry.sources,
        status: entry.status,
    };
}
//# sourceMappingURL=index.js.map